/** One event of the frozen LP-938 contract's `POST /v2/events` body. */
export interface QueuedEvent {
  name: string
  ts: string
  screen?: string
  props?: Record<string, string | number>
  anonymousId?: string
}

/** `true` send, `false` drop everything (flag off or opted out), `null` hold until we know (flags not loaded yet). */
export type QueueReadiness = boolean | null

export interface EventQueueOptions {
  /** A normal flush. Rejects with something carrying `status` (an `ApiError`) on failure. */
  send: (events: QueuedEvent[]) => Promise<unknown>
  /** The tab is going away: fire and forget, the request must outlive the page. */
  sendOnHide: (events: QueuedEvent[]) => void
  ready: () => QueueReadiness
  intervalMs?: number
  batchSize?: number
  cap?: number
}

/** The backend refuses a batch over 50 (400). */
export const MAX_PER_REQUEST = 50

/** In-memory queue (LP-947): flushed every 15 s, at 20 events and when the tab is hidden, never more than 200 kept. */
export function createEventQueue({ send, sendOnHide, ready, intervalMs = 15_000, batchSize = 20, cap = 200 }: EventQueueOptions) {
  let queue: QueuedEvent[] = []
  let inFlight = false
  let timer: ReturnType<typeof setInterval> | null = null

  const trim = () => {
    if (queue.length > cap) queue = queue.slice(queue.length - cap)
  }

  async function flush(): Promise<void> {
    const state = ready()
    if (state === false) queue = []
    if (state !== true || inFlight || queue.length === 0) return
    const batch = queue.splice(0, MAX_PER_REQUEST)
    inFlight = true
    try {
      await send(batch)
    } catch (error) {
      const status = (error as { status?: number } | null)?.status ?? 0
      // Offline, timed out or rate-limited: try again on the next flush. Any other refusal would fail again.
      if (status === 0 || status === 408 || status === 429 || status >= 500) {
        queue = [...batch, ...queue]
        trim()
      }
    } finally {
      inFlight = false
    }
  }

  function flushOnHide(): void {
    if (ready() !== true) return
    while (queue.length > 0) sendOnHide(queue.splice(0, MAX_PER_REQUEST))
  }

  function push(event: QueuedEvent): void {
    if (ready() === false) return
    queue.push(event)
    trim()
    if (queue.length >= batchSize) void flush()
  }

  const onVisibility = () => {
    if (document.visibilityState === 'hidden') flushOnHide()
  }

  return {
    push,
    flush,
    flushOnHide,
    clear: () => {
      queue = []
    },
    size: () => queue.length,
    start() {
      if (timer) return
      timer = setInterval(() => void flush(), intervalMs)
      document.addEventListener('visibilitychange', onVisibility)
    },
    stop() {
      if (timer) clearInterval(timer)
      timer = null
      document.removeEventListener('visibilitychange', onVisibility)
    },
  }
}

export type EventQueue = ReturnType<typeof createEventQueue>

/** The hidden-tab sender: `fetch` with `keepalive`, so the browser finishes the request after the page is gone. */
export function sendWithKeepalive(url: string, headers: Record<string, string>, events: QueuedEvent[]): void {
  try {
    void fetch(url, { method: 'POST', keepalive: true, headers: { 'Content-Type': 'application/json', ...headers }, body: JSON.stringify({ events }) }).catch(() => undefined)
  } catch {
    // No fetch, or the body is over the keepalive limit: these events are lost, nothing else breaks.
  }
}
